import type { ISignupCmd } from "@/data/commands/auth/SignupCmd";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { ServiceType } from "@/data/model/Enums";
import { exceptionTypes } from "@/data/util/Config";
import { Validation } from "@/data/util/Validation";

import { logError } from "@/shared/logging/errors";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { DomUtil } from "@/shared/ui/Helper";
import { Ripple } from "@/shared/ui/Ripple";

import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { DateInput } from "@/app/elements/DateInput";
import { EmailInput } from "@/app/elements/EmailInput";
import { PasswordInput } from "@/app/elements/PasswordInput";
import { PhoneNumberInput } from "@/app/elements/PhoneNumberInput";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export class SignupPopover extends BasePopover {
    private $socialLogins: JQuery;
    private $signupForm: JQuery;
    private $signupBtn: JQuery;
    private $emailRow: JQuery;
    private emailInput: EmailInput;
    private birthdayInput: DateInput;
    private passwordInput: PasswordInput;
    private phoneInput?: PhoneNumberInput;

    private useContactTracing?: boolean;
    private useCertificateVerification?: boolean;

    public onLoginClicked?: { () };

    constructor(userEmail?: string) {
        super(trans.signup.title);

        this.$element.addClass("signup");
        this.$signupForm = $('<form class="form" novalidate />').appendTo(this.$body.addClass("scrollable"));
        this.$signupForm.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSignup();

            return false;
        });

        this.$socialLogins = $('<div class="sociallogins" />').appendTo(this.$signupForm);

        Ripple.bindRippleOut($('<div class="sociallogin fb"><i class="fab fa-facebook-f"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuth("facebook");
            });

        Ripple.bindRippleOut($('<div class="sociallogin linkedin"><i class="fab fa-linkedin-in"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuth("linkedin");
            });

        Ripple.bindRippleOut($('<div class="sociallogin google"><i class="fab fa-google"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuth("google");
            });

        this.$signupForm.append('<div class="separator"><span>' + trans.signup.signupWithMail + "</span></div>");

        this.$emailRow = $('<div class="formrow" />').appendTo(this.$signupForm);
        this.emailInput = new EmailInput(true).appendTo(this.$emailRow);

        this.setPredefinedEmail(userEmail);

        let row = $('<div class="formrow" />').appendTo(this.$signupForm);
        this.passwordInput = new PasswordInput(true, true).appendTo(row);

        row = $('<div class="formrow splitted" />').appendTo(this.$signupForm);
        let col = $('<div class="col" />').appendTo(row);
        col.append(
            '<div class="formcontrol intent"><label>' +
                trans.signup.firstName +
                '</label><input type="text" name="firstname" value="' +
                (TastierClient.identityContext.getProfile()!.firstName !== undefined
                    ? TastierClient.identityContext.getProfile()!.firstName
                    : "") +
                '" /></div>'
        );

        col = $('<div class="col" />').appendTo(row);
        col.append(
            '<div class="formcontrol intent"><label>' +
                trans.signup.lastName +
                '</label><input type="text" name="lastname" value="' +
                (TastierClient.identityContext.getProfile()!.lastName !== undefined
                    ? TastierClient.identityContext.getProfile()!.lastName
                    : "") +
                '" /></div>'
        );

        row = $('<div class="formrow" />').appendTo(this.$signupForm);

        this.phoneInput = new PhoneNumberInput(true).appendTo(row);
        if (TastierClient.identityContext.getProfile()!.phoneNumber !== undefined)
            this.phoneInput.setValue(TastierClient.identityContext.getProfile()!.phoneNumber);

        row = $('<div class="formrow" />').appendTo(this.$signupForm);
        row.append(
            '<div class="formcontrol intent"><label id="ziplabel">' +
                trans.signup.zip +
                " (" +
                trans.general.optional +
                ')</label><input type="text" name="zip"  value="' +
                (TastierClient.identityContext.getProfile()!.zip !== undefined
                    ? TastierClient.identityContext.getProfile()!.zip
                    : "") +
                '" /></div>'
        );

        row = $('<div class="formrow" />').appendTo(this.$signupForm);
        this.birthdayInput = new DateInput(trans.covidSignupPopover.birthday, true, {
            startView: 2,
            defaultViewDate: new Date(new Date().getTime() - 86400000 * 30 * 365), // set default to 20 years ago,
            endDate: "today",
        }).appendTo(row);

        if (TastierClient.identityContext.getProfile()!.birthday !== undefined)
            this.birthdayInput.setValue(TastierClient.identityContext.getProfile()!.birthday);

        this.$signupForm.append('<div class="disclaimer">' + trans.signup.disclaimer + "</div>");

        this.$signupBtn = Ripple.bind(
            $('<button type="submit">' + trans.signup.submit + "</button>")
                .appendTo(this.$signupForm)
                .click((e) => {
                    e.preventDefault();
                    if (this.$signupBtn.hasClass("disabled") || this.$signupBtn.hasClass("loading")) {
                        e.stopPropagation();
                        return;
                    }

                    this.$signupForm.submit();
                })
        );

        FloatLabel.bind(this.$signupForm);

        $('<a href="#" class="signin">' + trans.signup.signin + "</a>")
            .appendTo(this.$body)
            .click((e) => {
                e.preventDefault();

                if (this.onLoginClicked !== undefined) this.onLoginClicked();
            });
    }

    protected onShow() {
        if (Helper.hasService(ServiceType.Covid)) {
            this.useCertificateVerification = Helper.getCovidServiceConfigValue<boolean>("verifyCovidCert") === true;
            this.useContactTracing = Helper.getCovidServiceConfigValue<boolean>("showContactTracing") === true;
        } else {
            this.useCertificateVerification = false;
            this.useContactTracing = false;
        }

        //update field labels
        this.$signupForm
            .find("#ziplabel")
            .html(trans.signup.zip + (this.useContactTracing ? "" : " (" + trans.general.optional + ")"));
        this.phoneInput!.setLabel(
            trans.covidSignupPopover.phone + (this.useContactTracing ? "" : " (" + trans.general.optional + ")")
        );
        this.birthdayInput.setLabel(
            trans.covidSignupPopover.birthday +
                (this.useCertificateVerification ? "" : " (" + trans.general.optional + ")")
        );
    }

    protected onHide() {
        this.destroy();
    }

    protected onDestroy() {
        if (this.phoneInput != undefined) {
            this.phoneInput.destroy();
            this.phoneInput = undefined;
        }
    }

    private async onSignup() {
        const firstNameField = this.$signupForm.find('input[name="firstname"]');
        const lastNameField = this.$signupForm.find('input[name="lastname"]');
        const zipField = this.$signupForm.find('input[name="zip"]');

        const data: ISignupCmd = {
            email: this.emailInput.getValue(),
            password: this.passwordInput.getValue(),
            firstName: firstNameField.val().length === 0 ? undefined : firstNameField.val(),
            lastName: lastNameField.val().length === 0 ? undefined : lastNameField.val(),
            phoneNumber: this.phoneInput!.getNumber(),
            phoneCountryCode: this.phoneInput!.getCountryCode(),
            birthday: this.birthdayInput.getValue(),
            zip: zipField.val().length === 0 ? undefined : zipField.val(),
        };

        let hasErrors = false;
        this.$signupForm.find(".has-error").removeClass("has-error");
        this.$emailRow.find("> .exception").remove();

        if (!this.emailInput.isValid()) {
            this.emailInput.setError(true);
            hasErrors = true;
        } else this.emailInput.setError(false);

        if (!this.passwordInput.isValid()) {
            this.passwordInput.setError(true);
            hasErrors = true;
        } else this.passwordInput.setError(false);

        if (
            data.firstName === undefined ||
            data.firstName.length < config.minNameLength ||
            data.firstName.length > config.maxNameLength
        ) {
            hasErrors = true;
            firstNameField.parent().addClass("has-error");
        }

        if (
            data.lastName === undefined ||
            data.lastName.length < config.minNameLength ||
            data.lastName.length > config.maxNameLength
        ) {
            hasErrors = true;
            lastNameField.parent().addClass("has-error");
        }

        if (data.phoneNumber !== undefined && !this.phoneInput!.isValid()) {
            hasErrors = true;
            this.phoneInput!.setError(true);
        } else this.phoneInput!.setError(false);

        if (data.zip !== undefined && !Validation.isZip(data.zip)) {
            hasErrors = true;
            zipField.parent().addClass("has-error");
        }

        if (this.useContactTracing && data.zip === undefined) {
            hasErrors = true;
            zipField.parent().addClass("has-error");
        }

        if (this.useContactTracing && data.phoneNumber === undefined) {
            hasErrors = true;
            this.phoneInput!.setError(true);
        }

        if (hasErrors) {
            const lastErrorField = this.$signupForm.find(".has-error").first();
            DomUtil.scrollTo(this.$body, lastErrorField);
            return;
        }

        this.$signupBtn.addClass("disabled loading");
        try {
            await TastierClient.identityContext.signup(data);
            this.hide();
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            this.$signupBtn.removeClass("disabled loading");

            if (e.type === exceptionTypes.emailInUseException) {
                this.$emailRow.append(`<div class="exception">${trans.signup.emailAlreadyUsed}</div>`);
                DomUtil.scrollTo(this.$body, this.$emailRow);
            } else {
                Popup.show(trans.signup.signupFailed);
            }
        }
    }

    private async externalAuth(provider: string) {
        try {
            await TastierClient.identityContext.authExternal(provider);
            this.hide();
        } catch (ex) {
            logError(ex);
            this.hide();
            Popup.show(trans.signin.externalAuthFailed);
        }
    }

    private setPredefinedEmail(userEmail: string | undefined) {
        if (userEmail != null) {
            this.emailInput.setValue(userEmail);
        } else {
            const profileEmail = TastierClient.identityContext.getProfile()!.email;
            if (profileEmail !== undefined) {
                this.emailInput.setValue(profileEmail);
            }
        }
    }

    public getInputEmail(): string | undefined {
        return this.emailInput.isValid() ? this.emailInput.getValue() : undefined;
    }
}
